/**
 * ProblemScreen (Screen 2)
 * 
 * Redesigned to match high-fidelity reference image.
 * Focuses on the "exhausting" feel of vocab prep and introduces the solution.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';

const QuoteItem = ({ icon, color, text, highlightedText, vp }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: `${vp.sp(12, 22, 32)}px 0`,
    borderBottom: '1px solid #F1F5F9',
    position: 'relative'
  }}>
    {/* Icon Container — Balanced scale */}
    <div style={{
      width: vp.sp(48, 56, 64),
      height: vp.sp(48, 56, 64),
      borderRadius: '50%',
      background: `${color}10`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {icon}
    </div>

    {/* Vertically centered line between icon and text (decorative from image) */}
    <div style={{ position: 'absolute', left: vp.sp(72, 80, 88), height: '28px', width: '1.5px', background: '#E2E8F0' }} />

    {/* Text Section — Slightly larger font */}
    <div style={{ flex: 1, paddingLeft: '16px', position: 'relative' }}>
      {/* Tiny Quote Mark at top left of text */}
      <div style={{
        position: 'absolute',
        top: '-4px',
        left: '0px',
        fontSize: '24px',
        color: color,
        opacity: 0.4,
        fontWeight: '900',
        fontFamily: 'serif'
      }}>“</div>

      <div style={{
        fontSize: vp.sp(14, 15, 16),
        color: '#1E293B',
        lineHeight: '1.4',
        fontWeight: '600',
        paddingLeft: '14px'
      }}>
        {text}
        <div style={{ color: color, fontWeight: '700' }}>{highlightedText}</div>
      </div>
    </div>
  </div>
);

export const ProblemScreen = ({ onNext, onBack }) => {
  const vp = useOnboardingViewport();
  const s = vp.spacing;

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: "#14B8A6",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: "0 8px 16px rgba(20, 184, 166, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  const isDesktop = vp.isTablet;
  const isSmallScreen = vp.isSmall;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.6s ease-out',
      position: 'relative',
      background: 'transparent',
      paddingBottom: vp.sp(20, 0, 0),
      transform: 'none'
    }}>
      {/* Decorative Stars (Top Right) */}
      <div style={{ position: 'absolute', top: '10%', right: '15%', fontSize: '20px', color: '#2DD4BF', opacity: 0.3 }}>✦</div>
      <div style={{ position: 'absolute', top: '16%', right: '8%', fontSize: '14px', color: '#2DD4BF', opacity: 0.2 }}>✦</div>

      {/* 1. Headline Area */}
      <div style={{ textAlign: 'left', marginTop: '0px', zIndex: 10 }}>
        <h2 style={{
          fontSize: vp.sp(28, 32, 36),
          fontWeight: '900',
          lineHeight: '1.1',
          color: '#0F172A',
          margin: 0,
          letterSpacing: '-1.3px'
        }}>
          Vocab prep<br />
          feels <span style={{ color: '#14B8A6' }}>exhausting.</span>
        </h2>
        <p style={{
          fontSize: vp.sp(15, 16, 17),
          color: '#64748B',
          marginTop: '8px',
          fontWeight: '500'
        }}>
          We get it. You’re not alone.
        </p>
      </div>

      {/* 2. Highlight Box — Moved UP further */}
      <div style={{
        marginTop: vp.sp(2, 4, 8),
        background: '#F0FDFB',
        padding: '12px 16px',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        border: '1px solid rgba(20, 184, 166, 0.05)'
      }}>
        <div style={{ color: '#14B8A6' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
        <div style={{ fontSize: vp.sp(12, 13, 14), color: '#007982', fontWeight: '600', lineHeight: '1.4' }}>
          You’re not lazy. Most methods just <br /><span style={{ fontWeight: '800' }}>don’t train memory properly.</span>
        </div>
      </div>

      {/* 3. Quotes Section */}
      <div style={{ marginTop: vp.sp(5, 10, 15) }}>
        <QuoteItem
          vp={vp}
          color="#14B8A6"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          }
          text="I studied 50 words yesterday. "
          highlightedText="Today I remember maybe 5.”"
        />
        <QuoteItem
          vp={vp}
          color="#8B5CF6"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-8 0 4 4 0 0 0-8 0 4 4 0 0 1-8 0" opacity="0.3" />
              <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0z" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
          }
          text="I know the word... "
          highlightedText="until I see it in a question.”"
        />
        <QuoteItem
          vp={vp}
          color="#F59E0B"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.5 19c.7 0 1.5-.3 2-1 .7-.8 1-1.7 1-2.6 0-2.4-2-4.4-4.5-4.4H15c-1.3-4.1-5-7-9-7-4.4 0-8 3.6-8 8 0 1.2.3 2.3.8 3.3.6 1.1 1.6 1.7 2.7 1.7" />
              <path d="M12 13v2M8 14v2M16 14v2" />
            </svg>
          }
          text="There are thousands of words. "
          highlightedText="I don’t even know where to begin.”"
        />
      </div>

      {/* 4. Solution Card — Balanced scale */}
      <div style={{
        marginTop: vp.sp(10, 16, 22),
        background: '#F8FAFC',
        borderRadius: '22px',
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        border: '1px solid #F1F5F9',
        minHeight: vp.sp(90, 100, 110)
      }}>
        <div style={{ flex: 1, zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={{ color: '#14B8A6' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ fontWeight: '800', fontSize: vp.sp(14, 15, 16), color: '#0F172A' }}>There’s a better way.</div>
          </div>
          <div style={{ fontSize: vp.sp(12, 13, 14), color: '#64748B', lineHeight: '1.4', fontWeight: '500', maxWidth: '180px' }}>
            We use games that train your brain to <span style={{ color: '#14B8A6', fontWeight: '800' }}>recall</span>, not just re-recognize.
          </div>
        </div>

        {/* Brain Mascot Placeholder — Balanced scale */}
        <div style={{
          position: 'absolute',
          right: '-5px',
          bottom: '-5px',
          width: vp.sp(90, 110, 130),
          height: 'auto',
          zIndex: 3
        }}>
          {/* Decorative lines near brain */}
          <div style={{ position: 'absolute', top: '-10px', right: '10px', opacity: 0.3 }}>
            <svg width="30" height="20" viewBox="0 0 40 30">
              <path d="M5 25L15 5M20 28L25 2M35 25L30 10" stroke="#64748B" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <img
            src="/images/onboarding/page2.webp"
            alt="Smart Brain"
            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {/* Fallback Box */}
          <div style={{ width: '100%', height: '100px', background: 'rgba(0,0,0,0.02)', borderRadius: '20px', display: 'none' }} />
        </div>
      </div>

      {/* 5. Bottom CTA */}
      <div style={{
        paddingTop: `${vp.sp(20, 30, 40)}px`,
        zIndex: 10
      }}>
        <button
          onClick={onNext}
          style={primaryCTAStyle}
        >
          Let's fix this
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ProblemScreen;
